import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Minus, Plus, ArrowLeft, ShoppingBag, Check } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function CartPage() {
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal } = useCart();
  const [step, setStep] = useState(1); // 1: Cart, 2: Info, 3: Confirm
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    customer_city: "",
    notes: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateItemPrice = (item) => {
    const multiplier = item.weight === 250 ? 2.2 : item.weight === 500 ? 4 : 1;
    return item.price * multiplier * item.quantity;
  };

  const handleSubmitOrder = async () => {
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.customer_address || !formData.customer_city) {
      toast.error("Lütfen tüm zorunlu alanları doldurun.");
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        ...formData,
        items: cartItems.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          weight: item.weight,
          price: item.price,
        })),
        total_amount: getCartTotal(),
      };

      await axios.post(`${API}/orders`, orderData);
      toast.success("Siparişiniz başarıyla alındı! En kısa sürede size ulaşacağız.");
      clearCart();
      setStep(3);
    } catch (error) {
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
      console.error("Failed to submit order:", error);
    } finally {
      setLoading(false);
    }
  };

  if (cartItems.length === 0 && step !== 3) {
    return (
      <div className="pt-24 min-h-screen bg-[#F9F7F2]" data-testid="empty-cart">
        <div className="section-container py-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <ShoppingBag className="w-16 h-16 text-[#C5A059] mx-auto mb-6" strokeWidth={1} />
            <h1 className="heading-lg text-[#0D3B2E] mb-4">Sepetiniz Boş</h1>
            <p className="body-text text-[#5D4037] mb-8">
              Henüz sepetinize ürün eklemediniz.
            </p>
            <Link to="/urunler" className="btn-primary inline-flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Ürünlere Göz At
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-[#F9F7F2]" data-testid="cart-page">
      {/* Header */}
      <section className="py-8 bg-white border-b border-[#E5E0D8]">
        <div className="section-container">
          <div className="flex items-center justify-between">
            <Link
              to="/urunler"
              className="inline-flex items-center gap-2 text-[#5D4037] hover:text-[#0D3B2E] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="caption-text">Alışverişe Devam</span>
            </Link>
            <h1 className="heading-md text-[#0D3B2E]">
              {step === 1 ? "Sepetim" : step === 2 ? "Teslimat Bilgileri" : "Sipariş Tamamlandı"}
            </h1>
            <div className="w-32" />
          </div>

          {/* Progress */}
          {step !== 3 && (
            <div className="flex items-center justify-center gap-4 mt-8">
              {[1, 2].map((s) => (
                <div key={s} className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 flex items-center justify-center border ${
                      step >= s
                        ? "bg-[#0D3B2E] border-[#0D3B2E] text-white"
                        : "border-[#E5E0D8] text-[#5D4037]"
                    }`}
                  >
                    {step > s ? <Check className="w-4 h-4" /> : s}
                  </div>
                  {s < 2 && (
                    <div
                      className={`w-16 h-[1px] ${
                        step > s ? "bg-[#0D3B2E]" : "bg-[#E5E0D8]"
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Step 1: Cart Items */}
      {step === 1 && (
        <section className="section-spacing" data-testid="cart-step-1">
          <div className="section-container">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-6">
                {cartItems.map((item) => (
                  <motion.div
                    key={`${item.product_id}-${item.weight}`}
                    {...fadeUp}
                    className="bg-white p-6 border border-[#E5E0D8] flex gap-6"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <Link to={`/urunler/${item.slug}`}>
                      <img
                        src={item.image_url}
                        alt={item.product_name}
                        className="w-24 h-24 object-cover"
                      />
                    </Link>
                    <div className="flex-1">
                      <div className="flex justify-between">
                        <div>
                          <Link
                            to={`/urunler/${item.slug}`}
                            className="heading-md text-[#0D3B2E] hover:text-[#C5A059] transition-colors"
                          >
                            {item.product_name}
                          </Link>
                          <p className="caption-text text-[#5D4037]/70 mt-1">
                            {item.weight}g
                          </p>
                        </div>
                        <button
                          onClick={() => removeFromCart(item.product_id, item.weight)}
                          className="text-[#5D4037] hover:text-red-500 transition-colors"
                          data-testid={`remove-${item.product_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.product_id,
                                item.weight,
                                item.quantity - 1
                              )
                            }
                            className="w-8 h-8 border border-[#E5E0D8] flex items-center justify-center hover:border-[#0D3B2E] transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="font-display text-lg w-8 text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() =>
                              updateQuantity(
                                item.product_id,
                                item.weight,
                                item.quantity + 1
                              )
                            }
                            className="w-8 h-8 border border-[#E5E0D8] flex items-center justify-center hover:border-[#0D3B2E] transition-colors"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                        </div>
                        <span className="font-display text-xl text-[#0D3B2E]">
                          ₺{calculateItemPrice(item).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <motion.div
                  {...fadeUp}
                  className="bg-white p-8 border border-[#E5E0D8] sticky top-32"
                >
                  <h2 className="heading-md text-[#0D3B2E] mb-6">Sipariş Özeti</h2>
                  <div className="space-y-4 mb-6">
                    <div className="flex justify-between body-text">
                      <span className="text-[#5D4037]">Ara Toplam</span>
                      <span className="text-[#0D3B2E]">₺{getCartTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between body-text">
                      <span className="text-[#5D4037]">Kargo</span>
                      <span className="text-[#C5A059]">Ücretsiz</span>
                    </div>
                  </div>
                  <div className="border-t border-[#E5E0D8] pt-6 mb-6">
                    <div className="flex justify-between">
                      <span className="heading-md text-[#0D3B2E]">Toplam</span>
                      <span className="font-display text-2xl text-[#0D3B2E]" data-testid="cart-total">
                        ₺{getCartTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => setStep(2)}
                    className="btn-gold w-full"
                    data-testid="proceed-to-checkout"
                  >
                    Sipariş Ver
                  </button>
                  <p className="caption-text text-[#5D4037]/70 text-center mt-4 text-[10px]">
                    * Demo sipariş sistemi - gerçek ödeme alınmaz
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Step 2: Customer Info */}
      {step === 2 && (
        <section className="section-spacing" data-testid="cart-step-2">
          <div className="section-container">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Form */}
              <div className="lg:col-span-2">
                <motion.div {...fadeUp} className="bg-white p-8 border border-[#E5E0D8]">
                  <h2 className="heading-md text-[#0D3B2E] mb-6">Teslimat Bilgileri</h2>
                  <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <input
                        type="text"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        placeholder="Ad Soyad *"
                        className="input-premium"
                        required
                        data-testid="input-customer-name"
                      />
                      <input
                        type="email"
                        name="customer_email"
                        value={formData.customer_email}
                        onChange={handleChange}
                        placeholder="E-posta *"
                        className="input-premium"
                        required
                        data-testid="input-customer-email"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <input
                        type="tel"
                        name="customer_phone"
                        value={formData.customer_phone}
                        onChange={handleChange}
                        placeholder="Telefon *"
                        className="input-premium"
                        required
                        data-testid="input-customer-phone"
                      />
                      <input
                        type="text"
                        name="customer_city"
                        value={formData.customer_city}
                        onChange={handleChange}
                        placeholder="Şehir *"
                        className="input-premium"
                        required
                        data-testid="input-customer-city"
                      />
                    </div>
                    <textarea
                      name="customer_address"
                      value={formData.customer_address}
                      onChange={handleChange}
                      placeholder="Adres *"
                      rows={3}
                      className="input-premium w-full resize-none"
                      required
                      data-testid="input-customer-address"
                    />
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Sipariş Notu (opsiyonel)"
                      rows={2}
                      className="input-premium w-full resize-none"
                      data-testid="input-notes"
                    />
                  </div>
                </motion.div>
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <motion.div
                  {...fadeUp}
                  className="bg-white p-8 border border-[#E5E0D8] sticky top-32"
                >
                  <h2 className="heading-md text-[#0D3B2E] mb-6">Sipariş Özeti</h2>
                  <div className="space-y-3 mb-6">
                    {cartItems.map((item) => (
                      <div
                        key={`${item.product_id}-${item.weight}`}
                        className="flex justify-between text-sm"
                      >
                        <span className="text-[#5D4037]">
                          {item.product_name} ({item.weight}g) x{item.quantity}
                        </span>
                        <span className="text-[#0D3B2E]">
                          ₺{calculateItemPrice(item).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-[#E5E0D8] pt-6 mb-6">
                    <div className="flex justify-between">
                      <span className="heading-md text-[#0D3B2E]">Toplam</span>
                      <span className="font-display text-2xl text-[#0D3B2E]">
                        ₺{getCartTotal().toFixed(2)}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <button
                      onClick={handleSubmitOrder}
                      disabled={loading}
                      className="btn-gold w-full disabled:opacity-50"
                      data-testid="submit-order"
                    >
                      {loading ? (
                        <span className="spinner mx-auto" />
                      ) : (
                        "Siparişi Tamamla"
                      )}
                    </button>
                    <button
                      onClick={() => setStep(1)}
                      className="btn-outline w-full"
                      data-testid="back-to-cart"
                    >
                      Sepete Dön
                    </button>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Step 3: Confirmation */}
      {step === 3 && (
        <section className="section-spacing" data-testid="cart-step-3">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-xl mx-auto text-center bg-white p-12 border border-[#E5E0D8]"
            >
              <div className="w-20 h-20 bg-[#0D3B2E] mx-auto mb-8 flex items-center justify-center">
                <Check className="w-10 h-10 text-[#C5A059]" />
              </div>
              <h1 className="heading-lg text-[#0D3B2E] mb-4">
                Siparişiniz Alındı!
              </h1>
              <p className="body-text text-[#5D4037] mb-8">
                Sipariş talebiniz başarıyla alındı. En kısa sürede sizinle
                iletişime geçeceğiz.
              </p>
              <p className="caption-text text-[#5D4037]/70 mb-8 text-[10px]">
                * Bu bir demo sipariş sistemidir. Gerçek ödeme alınmamıştır.
              </p>
              <Link to="/urunler" className="btn-primary inline-flex items-center gap-2">
                <ShoppingBag className="w-4 h-4" />
                Alışverişe Devam Et
              </Link>
            </motion.div>
          </div>
        </section>
      )}
    </div>
  );
}
